import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Breadcrumbs,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  useTheme,
  TextField
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Layout from '../../components/layout/Layout';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import axios from 'axios';
import API_CONFIG from '../../config/api.config';
import { getAuthHeader } from '../../services/authService';

const InvoiceProfitLossReport = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  // Get current date and first day of current month
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  };

  const getCurrentMonthStart = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return firstDay.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  };

  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isExporting, setIsExporting] = useState(false);
  
  // Search and filter states - Initialize with current date only (not whole month)
  const [startDate, setStartDate] = useState(getCurrentDate());
  const [endDate, setEndDate] = useState(getCurrentDate());
  const [searchQuery, setSearchQuery] = useState('');
  
  // Data state
  const [data, setData] = useState({
    totalInvoices: 0,
    totalRevenue: 0,
    totalProfit: 0,
    totalLoss: 0,
    invoiceDetails: []
  });

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVOICE_PROFIT_LOSS_REPORT}`, {
        headers: getAuthHeader(),
        params: {
          startDate,
          endDate,
          searchQuery
        }
      });
      
      // Use the data directly from the backend (which now handles return invoices properly)
      setData(response.data);
    } catch (err) {
      setError('Failed to fetch report data. Please try again.');
      enqueueSnackbar('Error generating report', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = async () => {
    if (data.invoiceDetails.length === 0) {
      enqueueSnackbar('No data to export', { variant: 'warning' });
      return;
    }

    setIsExporting(true);
    try {
      // Process the data for export - exclude return invoices
      const exportData = data.invoiceDetails.map(invoice => {
        return {
          'Invoice No': invoice.invoiceNo,
          'Date': formatDate(invoice.date),
          'Customer': invoice.customerName,
          'Revenue ($)': invoice.revenue,
          'Cost ($)': invoice.cost,
          'Profit/Loss ($)': invoice.profit === null ? 'N/A' : invoice.profit,
          'Margin (%)': invoice.margin === null ? 'N/A' : invoice.margin.toFixed(2)
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoice P&L Report');
      XLSX.writeFile(workbook, 'invoice_profit_loss_report.xlsx');
      enqueueSnackbar('Report exported successfully', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to export report', { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    handleSearch();
  }, []);

  return (
    <Layout>
      <Box component="main" sx={{ flexGrow: 1, py: 8 }}>
        <Container maxWidth="xl">
          {/* Breadcrumbs */}
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
            <Link component={RouterLink} to="/" color="inherit">Dashboard</Link>
            <Link component={RouterLink} to="/reports" color="inherit">Reports</Link>
            <Typography color="text.primary">Invoice-based P&L Report</Typography>
          </Breadcrumbs>

          <Typography variant="h4" gutterBottom>
            Invoice-based Profit & Loss Report
          </Typography>

          {/* Export Button Row */}
          <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
            <Grid item>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleExportToExcel}
                disabled={isExporting || data.invoiceDetails.length === 0}
                startIcon={isExporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                sx={{ minWidth: 180, height: '48px', fontWeight: 600 }}
              >
                {isExporting ? 'Exporting...' : 'Export to Excel'}
              </Button>
            </Grid>
          </Grid>

          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                bgcolor: '#1a237e', // Deep blue
                color: 'white',
                boxShadow: 3,
                '&:hover': { boxShadow: 6 }
              }}>
                <CardContent>
                  <Typography color="inherit" gutterBottom variant="subtitle2">
                    Total Invoices
                  </Typography>
                  <Typography color="inherit" variant="h4">
                    {loading ? <CircularProgress size={20} /> : data.invoiceDetails.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                bgcolor: '#e65100', // Deep orange
                color: 'white',
                boxShadow: 3,
                '&:hover': { boxShadow: 6 }
              }}>
                <CardContent>
                  <Typography color="inherit" gutterBottom variant="subtitle2">
                    Total Invoice Amount
                  </Typography>
                  <Typography color="inherit" variant="h4">
                    {loading ? <CircularProgress size={20} /> : `$${data.totalRevenue.toLocaleString()}`}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                bgcolor: '#1b5e20', // Deep green
                color: 'white',
                boxShadow: 3,
                '&:hover': { boxShadow: 6 }
              }}>
                <CardContent>
                  <Typography color="inherit" gutterBottom variant="subtitle2">
                    Total Profit
                  </Typography>
                  <Typography color="inherit" variant="h4">
                    {loading ? <CircularProgress size={20} /> : `$${data.totalProfit.toLocaleString()}`}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                bgcolor: '#b71c1c', // Deep red
                color: 'white',
                boxShadow: 3,
                '&:hover': { boxShadow: 6 }
              }}>
                <CardContent>
                  <Typography color="inherit" gutterBottom variant="subtitle2">
                    Total Loss
                  </Typography>
                  <Typography color="inherit" variant="h4">
                    {loading ? <CircularProgress size={20} /> : `$${data.totalLoss.toLocaleString()}`}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Search Form */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Filter Options
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Showing data from {startDate} to {endDate} (Today's data by default)
            </Typography>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  helperText="From date"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  helperText="To date"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Search by Invoice/Customer"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Enter invoice number or customer name"
                  helperText="Optional: Filter by invoice or customer"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSearch}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ height: '56px' }}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Results Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice No</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">Cost</TableCell>
                  <TableCell align="right">Profit/Loss</TableCell>
                  <TableCell align="right">Margin (%)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : data.invoiceDetails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No data available. Please search with different criteria.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.invoiceDetails
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((invoice) => {
                      return (
                        <TableRow key={invoice.invoiceNo}>
                          <TableCell>{invoice.invoiceNo}</TableCell>
                          <TableCell>{formatDate(invoice.date)}</TableCell>
                          <TableCell>{invoice.customerName}</TableCell>
                          <TableCell align="right">${invoice.revenue.toLocaleString()}</TableCell>
                          <TableCell align="right">${invoice.cost.toLocaleString()}</TableCell>
                          <TableCell 
                            align="right"
                            sx={{ 
                              color: invoice.profit >= 0 ? '#1b5e20' : '#b71c1c',
                              fontWeight: 'bold'
                            }}
                          >
                            {invoice.profit !== null ? `$${invoice.profit.toLocaleString()}` : 'N/A'}
                          </TableCell>
                          <TableCell 
                            align="right"
                            sx={{ 
                              color: invoice.margin >= 0 ? '#1b5e20' : '#b71c1c'
                            }}
                          >
                            {invoice.margin !== null ? `${invoice.margin.toFixed(2)}%` : 'N/A'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={data.invoiceDetails.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </Container>
      </Box>
    </Layout>
  );
};

export default InvoiceProfitLossReport; 